import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { isApplianceTenant } from '@a0/support-center-common/lib/tenants';
import getQuotaPercentage from '../../utils/getQuotaPercentage';


import { 
  DETAILED_REPORT_TYPES as T,
  isMixedPlan
} from '../../utils/resources/reports';

import ReportDetailsEnterpriseMFA from '../ReportDetailsEnterpriseMFA';
import ReportDetailsProMFA from '../ReportDetailsProMFA';
import ReportDetailsMachineToMachineAuth from '../ReportDetailsMachineToMachineAuth';
import ReportDetailsExternalUsers from '../ReportDetailsExternalUsers';
import ReportDetailsRegularUsers from '../ReportDetailsRegularUsers';
import ReportDetailsEnterpriseUsers from '../ReportDetailsEnterpriseUsers';
import ReportDetailsEmployeeUsers from '../ReportDetailsEmployeeUsers';
import ReportDetailsEnterpriseConnections from '../ReportDetailsEnterpriseConnections';

export const CLOUD = 'cloud';
export const APPLIANCE = 'appliance';

const reportTypeToComponentMap = {
  [T.EMPLOYEE_USERS]: { 
    component: ReportDetailsEmployeeUsers, 
    quotaField: 'employeeUsers',
    chartType: 'quotaEmployeeActiveUsers'
  },
  [T.EXTERNAL_ACTIVE_USERS]: { 
    component: ReportDetailsExternalUsers, 
    quotaField: 'externalUsers',
    chartType: 'quotaExternalActiveUsers'
  },
  [T.ENTERPRISE_ACTIVE_USERS]: { 
    component: ReportDetailsEnterpriseUsers, 
    quotaField: 'enterpriseUsers',
    chartType: 'quotaEnterpriseActiveUsers'
  },
  [T.ENTERPRISE_CONNECTIONS_USERS]: { 
    component: ReportDetailsEnterpriseConnections, 
    quotaField: 'connections',
    chartType: 'quotaEnterpriseConnections'
  },
  [T.REGULAR_ACTIVE_USERS]: { 
    component: ReportDetailsRegularUsers,
    quotaField: 'regularUsers',
    chartType: 'quotaRegularActiveUsers'
  },

  // TODO: we should redirect legacy routes to new ones...
  [T.ENTERPRISE_MFA_FEATURE]: { 
    component: ReportDetailsEnterpriseMFA, 
  },
  [T.MACHINE_TO_MACHINE_FEATURE]: { 
    component: ReportDetailsMachineToMachineAuth, 
  },
  [T.PRO_MFA_FEATURE]: { 
    component: ReportDetailsProMFA, 
  }
};

const getComponentByReportType = (reportType) => {
  return get(reportTypeToComponentMap, [reportType, 'component']);
};

const getMonth = (data) => {
  return get(data, 'overview.month');
};

const getCharts = (reportType, data, tenantId) => {
  const { quotaField, chartType } = get(reportTypeToComponentMap, reportType, {});

  if (quotaField && chartType) {
    const base = {
      type: chartType,
      quota: get(data, `overview.quota.${quotaField}`),
      percentage: get(data, `overview.percentage.${quotaField}`)
    };

    if (reportType === T.ENTERPRISE_CONNECTIONS_USERS) {
      return [
        {
          ...base,
          currentQuota: get(data, `overview.total.${quotaField}.enterprise`, 0)
        }
      ];
    } else {
      return [
        {
          ...base,
          currentQuota: get(data, `overview.total.${quotaField}`),
          percentageDetails: isApplianceTenant(tenantId)
            ? {
                appliance: getQuotaPercentage(
                  get(data, `overview.${APPLIANCE}.${quotaField}`),
                  get(data, `overview.quota.${quotaField}`)
                ),
                cloud: getQuotaPercentage(
                  get(data, `overview.${CLOUD}.${quotaField}`),
                  get(data, `overview.quota.${quotaField}`)
                )
              }
            : null,
          currentQuotaDetails: isApplianceTenant(tenantId)
            ? {
                appliance: get(data, `overview.${APPLIANCE}.${quotaField}`),
                cloud: get(data, `overview.${CLOUD}.${quotaField}`)
              }
            : null
        }
      ];
    }
  }
};

const ReportDetailsContainer = (props) => {
  const { reportType, data, tenantId, recordsPath } = props;

  let Component = getComponentByReportType(reportType);
  const charts = props.charts || getCharts(reportType, data, tenantId);

  if (Component) {
    return <Component 
      month={getMonth(data)}
      isAppliance={isApplianceTenant(tenantId)}
      isMixedPlan={isMixedPlan(data)}
      records={get(data, `details.${CLOUD}.${recordsPath}`)}
      applianceRecords={get(data, `details.${APPLIANCE}.${recordsPath}`)}
      charts={charts}
      {...props} 
    />;
  } else {
    return null;
  }
};

ReportDetailsContainer.propTypes = {
  accountName: PropTypes.string,
  activePage: PropTypes.number.isRequired,
  applianceUsageRecords: PropTypes.array,
  charts: PropTypes.array,
  contactSales: PropTypes.func,
  contactSalesLink: PropTypes.string,
  data: PropTypes.object.isRequired,
  filter: PropTypes.func,
  filtering: PropTypes.object,
  handleClickBackToAllReports: PropTypes.func,
  paginate: PropTypes.func,
  quantityName: PropTypes.string,
  recordsPath: PropTypes.string.isRequired,
  reportType: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired
};

ReportDetailsContainer.defaultProps = {
  activePage: 1,
  tenantId: ''
};

export default ReportDetailsContainer;
